import debug from 'debug';
import throttle from 'lodash.throttle';
import { useCallback, useEffect, useMemo } from 'react';
import { useInterval } from 'react-use';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { CHECK_INACTIVITY_INTERVAL_MS, INACTIVITY_TIMEOUT_MS, LOCAL_EVENT, STORAGE_EVENT, UPDATE_ACTIVITY_INTERVAL_MS, } from '@/config/constants';
import { isTestSelector, widgetIdSelector } from '@/state/config';
import { chatModalState, hasEventsSelector, lastActivityInWidgetState, } from '@/state/session';
import * as safeStorage from '@/util/safe-storage';
import { getTimestamp } from '@/util/utils';
import { useEventListener } from './useEventListener';
export const actKey = (integrationId) => `moveo_${integrationId}_last_activity`;
/**
 *
 * This hook helps to track a user's inactivity on a webpage over a specific time period.
 *
 * The tracked activity includes various user events like loading of the page,
 * mouse movements, mouse clicks, touch events, and keyboard inputs.
 *
 * It uses local storage to persist the timestamp of the user's last activity.
 *
 * If the user is inactive over a specified period of time, it triggers a 'ChatModal'
 * inactivity event.
 *
 */
export const useActivityTracker = (integrationId, timeout = INACTIVITY_TIMEOUT_MS) => {
    const key = actKey(integrationId);
    const [chatModal, setChatModal] = useRecoilState(chatModalState);
    const hasEvents = useRecoilValue(hasEventsSelector);
    const widgetId = useRecoilValue(widgetIdSelector);
    const setLastActivityInWidget = useSetRecoilState(lastActivityInWidgetState);
    const log = useMemo(() => debug(`moveo:iframe:${widgetId}:activity-tracker`), [widgetId]);
    const isTest = useRecoilValue(isTestSelector);
    const checkExpirationTime = useCallback(() => {
        const lastActivity = parseInt(safeStorage.getItem(key) || '0', 10);
        const expirationTime = lastActivity + timeout;
        if (lastActivity && expirationTime < getTimestamp()) {
            if (hasEvents && !chatModal) {
                setChatModal("inactivity" /* ChatModal.Inactivity */);
            }
        }
    }, [timeout, key, chatModal, hasEvents, setChatModal]);
    useInterval(isTest ? () => { } : checkExpirationTime, CHECK_INACTIVITY_INTERVAL_MS);
    const updateTime = useCallback(() => {
        const now = getTimestamp();
        safeStorage.setItem(key, '' + now);
    }, [key]);
    const throttleUpdateExpirationTime = throttle(updateTime, UPDATE_ACTIVITY_INTERVAL_MS);
    const handleStorageChange = useCallback((event) => {
        const e = event;
        // Ignore events for other keys
        if (e.key !== key || !e.newValue) {
            return;
        }
        log('Set last activity in recoil', e.newValue);
        setLastActivityInWidget(parseInt(e.newValue, 10));
    }, [log, key, setLastActivityInWidget]);
    useEventListener(STORAGE_EVENT, handleStorageChange);
    useEventListener(LOCAL_EVENT, handleStorageChange);
    useEffect(() => {
        window.addEventListener('load', throttleUpdateExpirationTime);
        window.addEventListener('mousemove', throttleUpdateExpirationTime);
        window.addEventListener('mousedown', throttleUpdateExpirationTime);
        window.addEventListener('touchstart', throttleUpdateExpirationTime);
        window.addEventListener('touchmove', throttleUpdateExpirationTime);
        window.addEventListener('click', throttleUpdateExpirationTime);
        window.addEventListener('scroll', throttleUpdateExpirationTime, true);
        window.addEventListener('keydown', throttleUpdateExpirationTime);
        return () => {
            window.removeEventListener('load', throttleUpdateExpirationTime);
            window.removeEventListener('mousemove', throttleUpdateExpirationTime);
            window.removeEventListener('mousedown', throttleUpdateExpirationTime);
            window.removeEventListener('touchstart', throttleUpdateExpirationTime);
            window.removeEventListener('touchmove', throttleUpdateExpirationTime);
            window.removeEventListener('click', throttleUpdateExpirationTime);
            window.removeEventListener('scroll', throttleUpdateExpirationTime, true);
            window.removeEventListener('keydown', throttleUpdateExpirationTime);
        };
    }, [throttleUpdateExpirationTime]);
};
